import { CheckedIcon, UnCheckedIcon } from '@components/icon/svg';
import { useEntitlementError } from '@context/EntitlementContext';
import { useFetchTranscriptListPagination, useWorkspaceUsage } from '@hooks/getQueries';
import { useQueueTranscriptWorkflow } from '@hooks/postQueries';
import useWorkspace from '@hooks/useWorkspace';
import { DataGrid } from '@mui/x-data-grid';
import {
  DEFAULT_REDACTION_POLICIES,
  TRANSCRIPT_LANGUAGE_MODE_OPTIONS,
  TRANSCRIPT_LANGUAGE_OPTIONS,
  TRANSCRIPT_REDACTION_POLICY_OPTIONS,
  TRANSCRIPT_WORKFLOW_OPTIONS,
  formatLanguageOption,
  normalizeLanguageArray,
  normalizeLanguageCode,
} from '@constants/transcriptWorkflows';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Link,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const STATUS_OPTIONS = [
  { value: 'completed', label: 'Complete' },
  { value: 'incomplete', label: 'Incomplete' },
];

const SOURCE_OPTIONS = [
  {
    value: 'original',
    label: 'Original',
    tooltip: 'Transcripts from direct interviews or direct uploads',
  },
  {
    value: 'derived',
    label: 'Derived',
    tooltip: 'Transcripts that are the result of Transcript Workflows',
  },
];
import { formatDate } from '@utils/dateFormat';
import { showAsyncTaskToast, showErrorToast } from '@utils/toast';
import { useEffect, useMemo, useState } from 'react';

const ENTITLEMENTS_DOC_URL = 'https://qualz.ai/docs/userguide/entitlements/';

const TranscriptWorkflowModal = ({ open, onClose, studyId, defaultTranscriptId, onQueued }) => {
  const { activeWorkspaceId } = useWorkspace();
  const { data: usageData } = useWorkspaceUsage(activeWorkspaceId);
  const { handleApiError } = useEntitlementError();
  const { mutateAsync: queueTranscriptWorkflow } = useQueueTranscriptWorkflow({
    studyId,
    silentSuccess: true,
  });

  const [activeStep, setActiveStep] = useState(0);
  const [selectedTranscriptIds, setSelectedTranscriptIds] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [workflowType, setWorkflowType] = useState('translate');
  const [sourceLanguageMode, setSourceLanguageMode] = useState('explicit');
  const [sourceLanguageCode, setSourceLanguageCode] = useState('en');
  const [codeSwitchLanguageCodes, setCodeSwitchLanguageCodes] = useState(['en', 'es']);
  const [targetLanguageCode, setTargetLanguageCode] = useState('es');
  const [customPrompt, setCustomPrompt] = useState('');
  const [redactionPolicies, setRedactionPolicies] = useState(DEFAULT_REDACTION_POLICIES);
  const [redactionSubstitution, setRedactionSubstitution] = useState('entity_name');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState(['completed']);
  const [selectedSource, setSelectedSource] = useState(['original']);

  const statusFilter =
    selectedStatuses.length === 0 ? 'completed,incomplete' : selectedStatuses.join(',');

  // Source filter is server-side: empty = show all (both original + derived)
  const sourceFilter =
    selectedSource.length === 0 || selectedSource.length === SOURCE_OPTIONS.length
      ? ''
      : selectedSource.join(',');

  const queryParams = useMemo(
    () => ({
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
    }),
    [paginationModel.page, paginationModel.pageSize],
  );

  const {
    data: transcriptsResponse,
    isPending: isTranscriptListPending,
    isError: isTranscriptListError,
  } = useFetchTranscriptListPagination(studyId, [], queryParams, {}, statusFilter, sourceFilter);

  useEffect(() => {
    if (!open) return;

    setActiveStep(defaultTranscriptId ? 1 : 0);
    setSelectedTranscriptIds(defaultTranscriptId ? [defaultTranscriptId] : []);
    setPaginationModel({ page: 0, pageSize: 10 });
    setWorkflowType('translate');
    setSourceLanguageMode('explicit');
    setSourceLanguageCode('en');
    setCodeSwitchLanguageCodes(['en', 'es']);
    setTargetLanguageCode('es');
    setCustomPrompt('');
    setRedactionPolicies(DEFAULT_REDACTION_POLICIES);
    setRedactionSubstitution('entity_name');
    setIsSubmitting(false);
    setSelectedStatuses(['completed']);
    setSelectedSource(['original']);
  }, [defaultTranscriptId, open]);

  const selectedLanguageMode = TRANSCRIPT_LANGUAGE_MODE_OPTIONS.find(
    (option) => option.id === sourceLanguageMode,
  );
  const selectedTranscriptCount = selectedTranscriptIds.length;
  const selectedWorkflow = TRANSCRIPT_WORKFLOW_OPTIONS.find((option) => option.id === workflowType);
  const requiresTargetLanguage = workflowType === 'translate';
  const requiresRedactionPolicies = workflowType === 'pii_redaction';
  const shouldShowCustomPrompt = workflowType !== 'pii_redaction';
  const isCodeSwitchMissingEnglish =
    sourceLanguageMode === 'code_switch' &&
    !codeSwitchLanguageCodes.some((code) => code === 'en' || code.startsWith('en_'));
  const codeSwitchHasValidPair =
    sourceLanguageMode !== 'code_switch' ||
    (codeSwitchLanguageCodes.length === 2 && !isCodeSwitchMissingEnglish);

  const remainingCredits =
    typeof usageData?.entitlements?.transcription_uploads_remaining === 'number'
      ? usageData.entitlements.transcription_uploads_remaining
      : null;
  const hasEntitlementInfo = remainingCredits !== null && !usageData?.legacy;
  const requiredCredits = selectedTranscriptCount;
  const hasEnoughCredits = !hasEntitlementInfo || remainingCredits >= requiredCredits;
  const remainingAfterRun = hasEntitlementInfo ? remainingCredits - requiredCredits : null;
  const isBusy = isSubmitting;

  // Source filter is server-side — just group derived right after their parent for display
  const currentPageRows = (() => {
    const all = transcriptsResponse?.transcripts || [];
    const parents = all.filter((r) => !r.is_derived);
    const derivedByRoot = {};
    all
      .filter((r) => r.is_derived)
      .forEach((r) => {
        const rootId = r.root_qualitative_item_id || r.derived_from_qualitative_item_id;
        if (rootId) {
          if (!derivedByRoot[rootId]) derivedByRoot[rootId] = [];
          derivedByRoot[rootId].push(r);
        }
      });
    const grouped = [];
    parents.forEach((p) => {
      grouped.push(p);
      (derivedByRoot[p.id] || []).forEach((d) => grouped.push(d));
      delete derivedByRoot[p.id];
    });
    // Append any derived whose parent isn't on this page
    Object.values(derivedByRoot).forEach((children) => grouped.push(...children));
    return grouped;
  })();
  const currentPageIds = currentPageRows.map((row) => row.id);
  const currentPageSelection = selectedTranscriptIds.filter((id) => currentPageIds.includes(id));

  const handleRowSelectionChange = (newSelectionModel) => {
    const selectionFromOtherPages = selectedTranscriptIds.filter(
      (id) => !currentPageIds.includes(id),
    );
    setSelectedTranscriptIds([...selectionFromOtherPages, ...newSelectionModel]);
  };

  // Accumulate id → item_type across every page the user visits, so we
  // can tell whether the (possibly cross-page) selection includes any
  // document. Enhance transcription is invalid for documents — it
  // re-transcribes original audio/video, which documents don't have.
  const [rowTypeById, setRowTypeById] = useState({});
  useEffect(() => {
    const rows = transcriptsResponse?.transcripts || [];
    if (rows.length === 0) return;
    setRowTypeById((prev) => {
      const next = { ...prev };
      for (const row of rows) {
        next[row.id] = row.item_type || 'transcript';
      }
      return next;
    });
  }, [transcriptsResponse]);

  const selectionHasDocument = selectedTranscriptIds.some((id) => rowTypeById[id] === 'document');

  // If the user already had Enhance selected and then adds a document to
  // the selection, bounce them off the now-invalid workflow so they're
  // never stuck on a disabled card. Translate is the safe default.
  useEffect(() => {
    if (selectionHasDocument && workflowType === 'enhance_transcription') {
      setWorkflowType('translate');
    }
  }, [selectionHasDocument, workflowType]);

  const canContinueFromSelection = selectedTranscriptCount > 0;
  const canContinueFromConfiguration =
    (sourceLanguageMode !== 'explicit' || !!sourceLanguageCode.trim()) &&
    codeSwitchHasValidPair &&
    (!requiresTargetLanguage || !!targetLanguageCode.trim()) &&
    (!requiresRedactionPolicies || redactionPolicies.length > 0);

  const canStartWorkflow =
    canContinueFromSelection && canContinueFromConfiguration && hasEnoughCredits;

  const buildWorkflowPayload = () => ({
    workflow_type: workflowType,
    source_language_mode: sourceLanguageMode,
    source_language_code: sourceLanguageMode === 'explicit' ? sourceLanguageCode : null,
    code_switch_language_codes: sourceLanguageMode === 'code_switch' ? codeSwitchLanguageCodes : [],
    target_language_code: requiresTargetLanguage ? targetLanguageCode : null,
    custom_prompt: shouldShowCustomPrompt ? customPrompt.trim() || null : null,
    redaction_policy_names: requiresRedactionPolicies ? redactionPolicies : [],
    redaction_substitution: requiresRedactionPolicies ? redactionSubstitution : 'entity_name',
  });

  const handleSubmit = async () => {
    if (isSubmitting || !canStartWorkflow || selectedTranscriptCount === 0) return;

    const payload = buildWorkflowPayload();
    const queuedTranscriptIds = [];

    setIsSubmitting(true);

    try {
      for (const selectedTranscriptId of selectedTranscriptIds) {
        await queueTranscriptWorkflow({
          transcriptId: selectedTranscriptId,
          payload,
        });
        queuedTranscriptIds.push(selectedTranscriptId);
      }

      showAsyncTaskToast(
        queuedTranscriptIds.length === 1
          ? 'Transcript workflow has started.'
          : `Transcript workflow has started for ${queuedTranscriptIds.length} transcripts.`,
      );

      onQueued?.({
        queuedTranscriptIds,
        workflowType,
      });
      onClose();
    } catch (error) {
      if (queuedTranscriptIds.length > 0) {
        showErrorToast({
          detail: {
            message: `Queued ${queuedTranscriptIds.length} transcript workflows before another transcript failed. Review the remaining transcripts and retry if needed.`,
          },
        });
        onQueued?.({
          queuedTranscriptIds,
          workflowType,
        });
        onClose();
      } else if (error?.status === 402 || error?.response?.status === 402) {
        handleApiError(error);
      } else {
        showErrorToast({
          detail: {
            message:
              error?.detail ||
              error?.message ||
              'Unable to start transcript workflows. Please try again.',
          },
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const transcriptColumns = [
    {
      field: 'name',
      headerName: 'Transcript Name',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Link
          href={`/interview/${studyId}/transcripts/${params.id}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
            lineHeight: 1.4,
            py: 0.5,
          }}
        >
          {params.value || params.id}
        </Link>
      ),
    },
    {
      field: 'is_complete',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const isDerived = params.row?.is_derived;
        const isComplete = params.value === true;
        const label = isDerived ? 'Derived' : isComplete ? 'Complete' : 'Incomplete';
        const colors = isDerived
          ? { bg: '#e3f2fd', color: '#1565c0' }
          : isComplete
            ? { bg: '#e8f5e9', color: '#2e7d32' }
            : { bg: '#fff3e0', color: '#e65100' };
        return (
          <Chip
            label={label}
            size="small"
            sx={{ bgcolor: colors.bg, color: colors.color, fontSize: 11 }}
          />
        );
      },
    },
    {
      field: 'item_type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => {
        const isDocument = params.value === 'document';
        return (
          <Chip
            label={isDocument ? 'Document' : 'Transcript'}
            size="small"
            variant="outlined"
            sx={{
              fontSize: 11,
              bgcolor: isDocument ? '#f3e8ff' : '#eef2ff',
              color: isDocument ? '#6b21a8' : '#3730a3',
              borderColor: isDocument ? '#e9d5ff' : '#e0e7ff',
            }}
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Submission Date',
      flex: 0.8,
      minWidth: 160,
      renderCell: (params) => (params.value ? formatDate(params.value) : 'Unknown'),
    },
  ];

  return (
    <Dialog open={open} onClose={isBusy ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>Transcript Workflows</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            Select which transcripts you want to process, then configure the workflow you want to
            run. You will see the configuration options in the next screen.
          </Typography>

          <Box sx={{ width: { xs: '100%', md: '70%' }, mx: 'auto' }}>
            <Stepper activeStep={activeStep}>
              <Step>
                <StepLabel>Select Transcripts</StepLabel>
              </Step>
              <Step>
                <StepLabel>Configure Workflow</StepLabel>
              </Step>
              <Step>
                <StepLabel>Review And Run</StepLabel>
              </Step>
            </Stepper>
          </Box>

          {activeStep === 0 && (
            <Stack spacing={2}>
              <Alert severity="info">Choose the transcripts you want to process in this run.</Alert>

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Typography variant="h6">Select Transcripts</Typography>
                  <FormControl size="small" sx={{ width: 180 }}>
                    <Select
                      multiple
                      value={selectedStatuses}
                      onChange={(e) => setSelectedStatuses(e.target.value)}
                      displayEmpty
                      renderValue={(selected) => {
                        const label =
                          selected.length === 0
                            ? 'All'
                            : selected
                                .map((v) => STATUS_OPTIONS.find((o) => o.value === v)?.label)
                                .join(', ');
                        return `Status: ${label}`;
                      }}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Checkbox size="small" checked={selectedStatuses.includes(opt.value)} />
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ width: 200 }}>
                    <Select
                      multiple
                      value={selectedSource}
                      onChange={(e) => setSelectedSource(e.target.value)}
                      renderValue={(selected) => {
                        const label =
                          selected.length === 0 || selected.length === SOURCE_OPTIONS.length
                            ? 'All'
                            : selected
                                .map((v) => SOURCE_OPTIONS.find((o) => o.value === v)?.label)
                                .join(', ');
                        return `Source: ${label}`;
                      }}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {SOURCE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Checkbox size="small" checked={selectedSource.includes(opt.value)} />
                          <Box>
                            <Typography variant="body2">{opt.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {opt.tooltip}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {selectedTranscriptCount}{' '}
                  {selectedTranscriptCount === 1 ? 'transcript' : 'transcripts'} selected
                </Typography>
              </Box>

              {isTranscriptListPending ? (
                <Box py={6} display="flex" justifyContent="center">
                  <CircularProgress />
                </Box>
              ) : isTranscriptListError ? (
                <Alert severity="error">Unable to load transcripts for workflow selection.</Alert>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    '& .MuiDataGrid-root': {
                      overflowX: 'auto',
                    },
                  }}
                >
                  <DataGrid
                    autoHeight
                    getRowHeight={() => 'auto'}
                    sx={{ '& .MuiDataGrid-cell': { py: 1 } }}
                    rows={currentPageRows}
                    columns={transcriptColumns}
                    getRowId={(row) => row.id}
                    checkboxSelection
                    keepNonExistentRowsSelected
                    hideFooterSelectedRowCount
                    slots={{
                      baseCheckbox: (props) => (
                        <Checkbox
                          checkedIcon={<CheckedIcon />}
                          icon={<UnCheckedIcon />}
                          {...props}
                        />
                      ),
                    }}
                    rowSelectionModel={currentPageSelection}
                    onRowSelectionModelChange={handleRowSelectionChange}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) =>
                      setPaginationModel({
                        page: model.page,
                        pageSize: model.pageSize,
                      })
                    }
                    rowCount={transcriptsResponse?.pagination?.total || 0}
                    pageSizeOptions={[5, 10, 25]}
                    paginationMode="server"
                    disableRowSelectionOnClick
                    disableColumnMenu
                    disableColumnSelector
                    disableDensitySelector
                  />
                </Box>
              )}
            </Stack>
          )}

          {activeStep === 1 && (
            <Stack spacing={3}>
              <Alert severity="info">
                {selectedTranscriptCount}{' '}
                {selectedTranscriptCount === 1 ? 'transcript is' : 'transcripts are'} selected. The
                settings below will be applied to each selected transcript.
              </Alert>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                {TRANSCRIPT_WORKFLOW_OPTIONS.map((option) => {
                  // Enhance transcription re-transcribes original audio/video,
                  // which documents don't have — block it whenever the
                  // selection includes a document.
                  const isDisabled = option.id === 'enhance_transcription' && selectionHasDocument;
                  const isSelected = workflowType === option.id;

                  const card = (
                    <Card
                      key={option.id}
                      variant={isSelected ? 'elevation' : 'outlined'}
                      elevation={isSelected ? 3 : 0}
                      sx={{
                        flex: 1,
                        p: 2,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.55 : 1,
                        position: 'relative',
                        border: isSelected ? '2px solid' : '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        backgroundColor: isSelected
                          ? 'rgba(25, 118, 210, 0.08)'
                          : 'background.paper',
                        boxShadow: isSelected ? '0 0 0 3px rgba(25, 118, 210, 0.12)' : 'none',
                        transition: 'all 0.2s ease',
                      }}
                      onClick={() => {
                        if (isDisabled) return;
                        setWorkflowType(option.id);
                      }}
                    >
                      {isDisabled && (
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{ position: 'absolute', top: 8, right: 8, color: 'warning.main' }}
                        />
                      )}
                      <Stack spacing={1}>
                        {isSelected && (
                          <Typography variant="caption" color="primary.main" fontWeight={700}>
                            Selected workflow
                          </Typography>
                        )}
                        <Typography variant="subtitle1" fontWeight={600}>
                          {option.title}
                        </Typography>
                        <Typography variant="body2">{option.description}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.details}
                        </Typography>
                        <Typography variant="caption" color="primary.main" fontWeight={600}>
                          1 transcription credit per selected transcript
                        </Typography>
                      </Stack>
                    </Card>
                  );

                  // Wrap a disabled card in a Tooltip that explains why.
                  // ``span`` wrapper so the tooltip still fires over a
                  // non-interactive (disabled-feel) element.
                  return isDisabled ? (
                    <Tooltip
                      key={option.id}
                      arrow
                      title="Not available for documents — transcription enhancement re-transcribes original audio or video, which documents don't have. Use Translate or PII redaction instead."
                    >
                      <Box sx={{ flex: 1, display: 'flex' }}>{card}</Box>
                    </Tooltip>
                  ) : (
                    card
                  );
                })}
              </Stack>

              <Divider />

              <TextField
                select
                label="Source language handling"
                value={sourceLanguageMode}
                onChange={(event) => setSourceLanguageMode(event.target.value)}
                helperText={selectedLanguageMode?.helper}
              >
                {TRANSCRIPT_LANGUAGE_MODE_OPTIONS.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    <Box>
                      <Typography variant="body2">{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.helper}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              {sourceLanguageMode === 'explicit' && (
                <Autocomplete
                  options={TRANSCRIPT_LANGUAGE_OPTIONS}
                  freeSolo
                  value={
                    TRANSCRIPT_LANGUAGE_OPTIONS.find(
                      (option) => option.code === sourceLanguageCode,
                    ) || sourceLanguageCode
                  }
                  onChange={(_, value) => setSourceLanguageCode(normalizeLanguageCode(value))}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') {
                      setSourceLanguageCode(normalizeLanguageCode(value));
                    }
                  }}
                  getOptionLabel={formatLanguageOption}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Source language"
                      helperText="Use a supported language code. You can type one directly if it is not already in the list."
                    />
                  )}
                />
              )}

              {sourceLanguageMode === 'code_switch' && (
                <Autocomplete
                  multiple
                  options={TRANSCRIPT_LANGUAGE_OPTIONS}
                  value={codeSwitchLanguageCodes
                    .map((code) =>
                      TRANSCRIPT_LANGUAGE_OPTIONS.find((option) => option.code === code),
                    )
                    .filter(Boolean)}
                  onChange={(_, values) =>
                    setCodeSwitchLanguageCodes(normalizeLanguageArray(values))
                  }
                  getOptionLabel={formatLanguageOption}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Expected languages"
                      helperText="Choose exactly two languages. In this mode, one of them should be English."
                    />
                  )}
                />
              )}

              {requiresTargetLanguage && (
                <Autocomplete
                  options={TRANSCRIPT_LANGUAGE_OPTIONS}
                  freeSolo
                  value={
                    TRANSCRIPT_LANGUAGE_OPTIONS.find(
                      (option) => option.code === targetLanguageCode,
                    ) || targetLanguageCode
                  }
                  onChange={(_, value) => setTargetLanguageCode(normalizeLanguageCode(value))}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') {
                      setTargetLanguageCode(normalizeLanguageCode(value));
                    }
                  }}
                  getOptionLabel={formatLanguageOption}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Target language"
                      helperText="Run one target language per workflow. Each run creates one translated transcript per selected transcript."
                    />
                  )}
                />
              )}

              {requiresRedactionPolicies && (
                <>
                  <Autocomplete
                    multiple
                    options={TRANSCRIPT_REDACTION_POLICY_OPTIONS}
                    value={TRANSCRIPT_REDACTION_POLICY_OPTIONS.filter((option) =>
                      redactionPolicies.includes(option.value),
                    )}
                    onChange={(_, values) =>
                      setRedactionPolicies(values.map((value) => value.value))
                    }
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="PII categories to redact"
                        helperText="Choose the PII types to redact. Select at least one category."
                      />
                    )}
                  />
                  <TextField
                    select
                    label="Replacement style"
                    value={redactionSubstitution}
                    onChange={(event) => setRedactionSubstitution(event.target.value)}
                    helperText="Readable placeholders use labels like [PERSON_NAME]. Hash masks the original value with ####."
                  >
                    <MenuItem value="entity_name">Readable placeholders</MenuItem>
                    <MenuItem value="hash">Hash values</MenuItem>
                  </TextField>
                </>
              )}

              {shouldShowCustomPrompt && (
                <TextField
                  label="Optional custom prompt"
                  value={customPrompt}
                  onChange={(event) => setCustomPrompt(event.target.value.slice(0, 2000))}
                  multiline
                  minRows={4}
                  helperText={`${customPrompt.length}/2000 characters. Use this for terminology, spelling, or workflow-specific guidance. Avoid asking the workflow to summarize or change speaker intent.`}
                />
              )}

              {sourceLanguageMode === 'code_switch' && !codeSwitchHasValidPair && (
                <Alert severity="warning">
                  Choose exactly two expected languages and include English to use bilingual mode.
                </Alert>
              )}

              {workflowType === 'enhance_transcription' && (
                <Alert severity="info">
                  Each selected transcript will use the best available source. If only text is
                  available, Qualz will clean up the transcript text instead of rerunning
                  transcription.
                </Alert>
              )}

              {workflowType === 'pii_redaction' && (
                <Alert severity="warning">
                  PII redaction creates a new derived transcript. It does not delete the source
                  transcript, and existing summaries or analysis artifacts may still contain PII
                  until they are regenerated.
                </Alert>
              )}
            </Stack>
          )}

          {activeStep === 2 && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Card variant="outlined" sx={{ flex: 1, p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="h6">Workflow Summary</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Workflow
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {selectedWorkflow?.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Selected transcripts
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {selectedTranscriptCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Language handling
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {selectedLanguageMode?.label}
                    </Typography>
                    {requiresTargetLanguage && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Target language
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {formatLanguageOption(targetLanguageCode)}
                        </Typography>
                      </>
                    )}
                    {requiresRedactionPolicies && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          PII categories
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {redactionPolicies.length} selected
                        </Typography>
                      </>
                    )}
                    {shouldShowCustomPrompt && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Custom prompt
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {customPrompt.trim() ? 'Provided' : 'Not provided'}
                        </Typography>
                      </>
                    )}
                  </Stack>
                </Card>

                <Card variant="outlined" sx={{ flex: 1, p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="h6">Transcription Credits Required</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Calculation
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {selectedTranscriptCount} transcripts x 1 transcription credit each
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Total required
                    </Typography>
                    <Typography variant="h5" color="primary.main" fontWeight={700}>
                      {requiredCredits}
                    </Typography>
                    {hasEntitlementInfo ? (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Available now
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {remainingCredits}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Remaining after run
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          color={hasEnoughCredits ? 'success.main' : 'error.main'}
                        >
                          {remainingAfterRun}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Workspace entitlement balance is not available in this view. Qualz will
                        still validate transcription credits before processing starts.
                      </Typography>
                    )}
                    <Link href={ENTITLEMENTS_DOC_URL} target="_blank" underline="hover">
                      Learn more about entitlements
                    </Link>
                  </Stack>
                </Card>
              </Stack>

              {!hasEnoughCredits && (
                <Alert severity="error">
                  You need {requiredCredits} transcription credits for this run, but only{' '}
                  {remainingCredits} are currently available in the active workspace.
                </Alert>
              )}

              <Alert severity="info">
                You will receive a notification when each derived transcript is ready to review.
              </Alert>
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isBusy}>
          Cancel
        </Button>
        {activeStep > 0 && !(activeStep === 1 && defaultTranscriptId) && (
          <Button
            onClick={() => setActiveStep((current) => Math.max(0, current - 1))}
            disabled={isBusy}
          >
            Back
          </Button>
        )}
        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={() => setActiveStep(1)}
            disabled={!canContinueFromSelection || isBusy}
          >
            Continue
          </Button>
        )}
        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={() => setActiveStep(2)}
            disabled={!canContinueFromConfiguration || isBusy}
          >
            Review Run
          </Button>
        )}
        {activeStep === 2 && (
          <Button variant="contained" onClick={handleSubmit} disabled={!canStartWorkflow || isBusy}>
            {isBusy
              ? 'Starting workflows...'
              : selectedTranscriptCount === 1
                ? 'Run Workflow'
                : `Run Workflow for ${selectedTranscriptCount} Transcripts`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TranscriptWorkflowModal;
